/**
 * The on-device database.
 *
 * Everything the app reads comes from here, online or not. Four stores mirror the
 * four API resources; the `outbox` records which of those rows still have to reach
 * the server, and `meta` holds sync bookkeeping and the cached identity.
 *
 * Note what the outbox does *not* hold: the payload. Entries are keyed by
 * resource+id and the body is read out of the mirror at upload time, so recording
 * two hundred shots into one game leaves a single outbox row that always carries the
 * latest state. That works because the API is an upsert keyed on a client-owned id
 * (see GameService.save) — replaying a write is safe, and replaying a stale one is
 * the only thing we have to avoid.
 */

import { DBSchema, IDBPDatabase, openDB } from 'idb';
import { Game, PracticeSession } from '../types/curling';
import { CurlingClub } from '../data/curlingClubs';
import { CurrentUser } from '../types/auth';
import type { SavedTeam } from '../utils/storage';

/** The API resources that are mirrored locally. Also the outbox drain order. */
export const RESOURCES = ['clubs', 'savedTeams', 'games', 'practiceSessions'] as const;

export type Resource = (typeof RESOURCES)[number];

export type OutboxOp = 'upsert' | 'delete';

/**
 * `pending` entries are retried on every drain. `blocked` ones are not: the server
 * refused them for a reason retrying will not fix — no write access, or, once
 * data-entry credits exist, none left. They stay in the outbox so the data is not
 * lost and the user can be told, and go back to `pending` when they ask to retry.
 */
export type OutboxStatus = 'pending' | 'blocked';

export interface OutboxEntry {
  /** `${resource}:${id}` — the collapse key. One row per record, never a log. */
  key: string;
  resource: Resource;
  id: string;
  op: OutboxOp;
  /**
   * Account the record should be filed under, captured when the write happened.
   * The server ignores it for records that already exist.
   */
  accountId?: string;
  queuedAt: number;
  updatedAt: number;
  /**
   * Bumped on every local write to this record. An upload reads the body, awaits the
   * network, and only then clears the entry — by which time the user may have
   * recorded another shot into the same game. Clearing is therefore conditional on
   * the revision still matching, so that later edit survives to the next drain
   * instead of being dropped as "already uploaded".
   */
  revision: number;
  attempts: number;
  status: OutboxStatus;
  lastError?: string;
}

/** A practice session as stored: the API keys it by game, so gameId is required here. */
export type StoredPracticeSession = PracticeSession & { gameId: string };

/** What we remember about the signed-in user so the app still opens offline. */
export interface CachedIdentity {
  user: CurrentUser;
  cachedAt: number;
}

interface CurlingCodexDB extends DBSchema {
  games: { key: string; value: Game };
  savedTeams: { key: string; value: SavedTeam };
  clubs: { key: string; value: CurlingClub };
  practiceSessions: { key: string; value: StoredPracticeSession };
  outbox: {
    key: string;
    value: OutboxEntry;
    indexes: { byResource: Resource; byStatus: OutboxStatus };
  };
  meta: { key: string; value: unknown };
}

export type Db = IDBPDatabase<CurlingCodexDB>;

const DB_PREFIX = 'curlingcodex';
const DB_VERSION = 1;

let activeUserId: string | undefined;
let handle: Promise<Db> | undefined;

function open(userId: string): Promise<Db> {
  return openDB<CurlingCodexDB>(`${DB_PREFIX}_${userId}`, DB_VERSION, {
    upgrade(database) {
      database.createObjectStore('games', { keyPath: 'id' });
      database.createObjectStore('savedTeams', { keyPath: 'id' });
      database.createObjectStore('clubs', { keyPath: 'id' });
      database.createObjectStore('practiceSessions', { keyPath: 'gameId' });

      const outbox = database.createObjectStore('outbox', { keyPath: 'key' });
      outbox.createIndex('byResource', 'resource');
      outbox.createIndex('byStatus', 'status');

      database.createObjectStore('meta');
    },
    blocking() {
      // Another tab is upgrading. Let go so it can proceed rather than deadlocking
      // both; the next db() call reopens at the new version.
      handle = undefined;
    },
  });
}

/**
 * Opens (and memoises) the signed-in user's own database.
 *
 * @throws if called before {@link claimForUser}
 */
export function db(): Promise<Db> {
  if (!activeUserId) {
    throw new Error('offline db: claimForUser(userId) must be called before use');
  }
  if (!handle) {
    handle = open(activeUserId);
  }
  return handle;
}

/**
 * Points the local database at {@code userId}'s own store.
 *
 * Each user gets a separate IndexedDB database, named by their id, rather than one
 * shared database that gets wiped on every switch. That matters for two reasons this
 * app cannot compromise on: a device is often shared — a clubhouse tablet, a coach's
 * phone handed to a player from a different team — and must never surface one
 * person's cached games or roster to another; and a facility can go days without
 * connectivity, so a person's own queued-but-unsynced recordings must survive
 * whatever else happens on the device between now and whenever they next sign back
 * in on it, including someone else signing in and out in between. Switching users
 * therefore only ever changes which database this points at — it never deletes
 * anything.
 */
export function claimForUser(userId: string): void {
  if (activeUserId === userId) return;
  activeUserId = userId;
  const previous = handle;
  handle = undefined;
  // Close the outgoing user's connection instead of just dropping the reference —
  // on a shared device a tablet can cycle through many users in one browser
  // session, and an IndexedDB connection left open per switch would accumulate for
  // as long as the tab stays open.
  if (previous) {
    previous.then(database => database.close()).catch(() => {});
  }
}

// ------------------------------------------------------------------------ meta

const IDENTITY_KEY = 'identity';
const LAST_PULL_KEY = 'lastPulledAt';

export async function readMeta<T>(key: string): Promise<T | undefined> {
  return (await db()).get('meta', key) as Promise<T | undefined>;
}

export async function writeMeta(key: string, value: unknown): Promise<void> {
  await (await db()).put('meta', value, key);
}

export const readIdentity = (): Promise<CachedIdentity | undefined> =>
  readMeta<CachedIdentity>(IDENTITY_KEY);

export const writeIdentity = (identity: CachedIdentity): Promise<void> =>
  writeMeta(IDENTITY_KEY, identity);

export async function clearIdentity(): Promise<void> {
  await (await db()).delete('meta', IDENTITY_KEY);
}

export const readLastPulledAt = (): Promise<number | undefined> =>
  readMeta<number>(LAST_PULL_KEY);

export const writeLastPulledAt = (at: number): Promise<void> =>
  writeMeta(LAST_PULL_KEY, at);

/**
 * Drops this user's mirrored data and outbox, but keeps their cached identity. Not
 * called automatically — it exists for a person to deliberately clear their own
 * offline copy (public-computer hygiene, or troubleshooting), never as a side effect
 * of someone else signing in.
 */
export async function wipeData(): Promise<void> {
  const database = await db();
  const tx = database.transaction([...RESOURCES, 'outbox'], 'readwrite');
  await Promise.all([
    ...RESOURCES.map(resource => tx.objectStore(resource).clear()),
    tx.objectStore('outbox').clear(),
  ]);
  await tx.done;
  await (await db()).delete('meta', LAST_PULL_KEY);
}
